import React, { useState } from 'react'
import { User } from "types/user"
import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { DataTable, DataTableExpandedRows, DataTableFilterEvent, DataTableFilterMeta, DataTablePageEvent, DataTableValueArray } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import { getUsers, LazyTableState } from '_services/users.service';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Column, ColumnProps } from 'primereact/column';
import { Avatar } from 'primereact/avatar';
import { useFetchV2 } from '../../../../../_core/hooks'
import Await from '../../../../../_core/components/await';
import { Button } from 'primereact/button'
import { confirmDialog} from 'primereact/confirmdialog';
import { PopupContext, PopUpContextType} from '../../../../../_core/contexts/popToast'
import { mergeUsers } from '_services/admin.service';
import { ScrollPanel } from 'primereact/scrollpanel';
import { formatHeightMeasurement } from 'util/helpers.converters';


interface MergeUsersProps {
  primaryUser:User
  isVisible:boolean
  onHide: () => void
}
let filterDebounce: number | null = null

const MergeUsersdialog: React.FC<MergeUsersProps> = ({
  primaryUser,
  isVisible,
  onHide
}) => {
  const [globalFilterValue, setGlobalFilterValue] = useState<string>('')
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray | undefined>(undefined)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const { openPopup } = React.useContext<PopUpContextType>(PopupContext)

  const [lazyState, setlazyState] = useState<LazyTableState<DataTableFilterMeta>>({
    first             : 0,
    rows              : 10,
    page              : 0,
    filters: {
      globalFilter            : { value: '', matchMode: FilterMatchMode.CONTAINS },
      firstName               : { value: '', matchMode: FilterMatchMode.CONTAINS },
      lastName                : { value: '', matchMode: FilterMatchMode.CONTAINS },
      phone                   : { value: '', matchMode: FilterMatchMode.CONTAINS },
      email                   : { value: '', matchMode: FilterMatchMode.CONTAINS },
      occupationName          : { value: '', matchMode: FilterMatchMode.CONTAINS },
      aboutMe                 : { value: '', matchMode: FilterMatchMode.CONTAINS },
      highestEducationLevel   : { value: '', matchMode: FilterMatchMode.CONTAINS },
      status                  : { value: [], matchMode: FilterMatchMode.CUSTOM  },
    }
  })

  const [users, fetchUsers] = useFetchV2(
    () => getUsers<DataTableFilterMeta>(lazyState),
    undefined,
    [lazyState]
  )

  const onPage = (event: DataTablePageEvent) => {
    setlazyState((prev) => ({...prev,...event}))
  }

  const onFilter = (event: DataTableFilterEvent) => {
    event['first'] = 0
    event['page'] = 0
    setlazyState((prev => ({...prev,'filters':{...prev.filters,...event.filters}})))
  }

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilterValue(event.target.value)
    if(filterDebounce){
      window.clearTimeout(filterDebounce)
    }
    filterDebounce = window.setTimeout(() => {
    onFilter({filters: {'globalFilter': {value: event.target.value, matchMode: FilterMatchMode.CONTAINS}}})
    }, 1000)
  }

  const onSuccess = () => {
    openPopup({
      severity: 'success', title: 'Merge Successful!',
      text: `User Data for ${primaryUser.firstName} has been sucessfully merged!`, duration: 3000
    })
    fetchUsers(undefined)
    onHide()
  }

  const accept = () => {
    mergeUsers(primaryUser.id, selectedUser!.id,
      onSuccess,
      () => openPopup({ severity: 'warn', title: 'Error', text: `The merge has failed to process.\nPlease contact the development team to investigate.`, duration: 3000 })
    )
  }

  const reject = () => {

  }

  const OpenConfirmDialog = () => {
    confirmDialog({
      header: `Are you sure you want to merge ${selectedUser?.firstName}'s data into ${primaryUser.firstName}?`,
      message:
      <p>
        The following will happen if you Accept:
        <br />- Data including Notifications, Preferences, Metadata, Purchases, Likes, and Messages will be associated with the Primary user.
        <br />- The Secondary User's Phone number will be removed.
        <br />- The Secondary User's Account will be marked for future deletion.
        <br /><h3 style={{color:"red"}}>THIS CANNOT BE UNDONE.</h3>
      </p>,
      icon: <i className="pi pi-exclamation-triangle" style={{ color: 'red', fontSize: '2.5rem' }}></i>,
      acceptClassName: 'p-button-danger',
      defaultFocus: 'reject',
      accept,
      reject
    })
  }

  const columns: ColumnProps[] = [
    {
      field: '',
      header: 'Profile Photo',

      body: (data: User) =>
        <>
          <Avatar image={data.userPhotos && data.userPhotos[0]?.imgUrl}
                  imageFallback={data.userPhotos && data.userPhotos[0]?.imgUrl}
                  size="xlarge"
                  icon="pi pi-user"
                   />
        </>
    },
      {
        field: 'firstName',
        header: 'First Name',
        filter: true,
        filterPlaceholder: "First Name",
      },
      {
        field: 'lastName',
        header: 'Last Name',
        filter: true,
        filterPlaceholder: "Last Name",
      },
      {
        field: 'userMetadata.birthday',
        header: 'Date of Birth',
        filter: false,
        dataType: 'date',
        filterPlaceholder: "Birthday search",
      },
      {
        field: 'userGender',
        header: 'Gender',
        filter: false,
        filterPlaceholder: "Gender search",
        body:(data:User) => <>{data.userMetadata?.UserGender?.display}</>
      },
      {
        field: 'phone',
        header: 'Phone #',
        filter: true,
        filterPlaceholder: "Phone #",
        style: { width: '8rem' },
        body:(data: User) => <>{data.countryCode}+ {data.phone}</>
      },
      {
        field: 'email',
        header: 'Email',
        filter: true,
        filterPlaceholder: "Email",
      }
  ]

  const PrimaryCardColumn = (label:string, value:string | boolean| number | undefined) => {
    return (
      <div style={{marginBottom:"0.5rem"}}>
      <label style={{fontWeight:"bold"}}>{label}:</label>
      <div style={{marginLeft:"1rem"}}>{value || "N/A"}</div>
    </div>
    )
  }

  const PrimaryUserCard = (user:User) => {
    return (
      <Card className={"m-2"} title={"Current User Data"} style={{backgroundColor:"#f5f5f5", flex:"1 1 auto"}}>
          <div className={"p-fluid p-grid"} style={{height:"20vw"}}>
            <div className={"p-col"} style={{textAlign:"center"}}>
              <Avatar image={user?.userPhotos && user?.userPhotos[0]?.imgUrl}
              imageFallback={user?.userPhotos && user?.userPhotos[0]?.imgUrl}
              size="xlarge"
              icon="pi pi-user"
              />
            </div>
            <ScrollPanel style={{ width: '100%', height:"20vw"}}>
              <div style={{padding:"0.5rem"}}>
                {PrimaryCardColumn("DBG ID", user?.dbgId)}
                {PrimaryCardColumn("First Name", user?.firstName)}
                {PrimaryCardColumn("Last Name", user?.lastName)}
                {PrimaryCardColumn("Birthday", user?.username)}
                {PrimaryCardColumn("Phone", user?.phone)}
                {PrimaryCardColumn("Email", user?.email)}
                {PrimaryCardColumn("Last Logged In", user?.lastLoggedIn)}
                {PrimaryCardColumn("Country Friendly Name", user?.countryFriendlyName)}
                {PrimaryCardColumn("Country Code", user?.countryCode)}
                {PrimaryCardColumn("Approved At", user?.approvedAt)}
                {PrimaryCardColumn("Denied At", user?.deniedAt)}
                {PrimaryCardColumn("Occupation", user?.userMetadata?.occupationName)}
                {PrimaryCardColumn("Highest Education", user?.userMetadata?.highestEducationLevel)}
                {PrimaryCardColumn(
                  "Nationalities",
                  user?.userMetadata?.Nationalities?.map(x => x.EnumNationality.nationality).join(', ')
                )}
                {PrimaryCardColumn(
                  "Height",
                  user?.userMetadata?.heightMeasurement && formatHeightMeasurement(user.userMetadata?.heightMeasurement).dualLocaleDisplay
                )}
                {PrimaryCardColumn("Preferred Sex", user?.userMetadata?.preferredSex)}
                {PrimaryCardColumn("Has/Wants Kids", user?.userMetadata?.hasOrWantsKids)}
                {PrimaryCardColumn("Smokes Tobacco", user?.userMetadata?.smokesTobacco)}
                {PrimaryCardColumn("Smokes Weed", user?.userMetadata?.smokesWeed)}
                {PrimaryCardColumn("Drinks Alcohol", user?.userMetadata?.drinksAlcohol)}
                {PrimaryCardColumn("Religious Belief", user?.userMetadata?.religiousBeliefName)}
                {PrimaryCardColumn("Political Belief", user?.userMetadata?.politicalBeliefName)}
                {PrimaryCardColumn("Body Type", user?.userMetadata?.bodyTypeName)}
              </div>
            </ScrollPanel>
          </div>
        </Card>
    )
  }

  const ResultsCardColumn = (label:string, primaryValue:string | number | boolean | undefined, secondaryValue:string | number | boolean | undefined) => {
    return (<div style={{marginBottom:"0.5rem"}}>
      {(primaryValue) ?
      <>
        <label style={{ fontWeight: "bold" }}>{label}:</label>
        <div style={{ marginLeft: "1rem"}}>
        {primaryValue}
      </div></>
      :
      <><label style={{ fontWeight: "bold" }}>{secondaryValue && "* "}{label}:</label>
        <div style={{ marginLeft: "1rem", color: "green" }}>
          {secondaryValue}
        </div></>
      }
    </div>)
  }

  const ResultUserCard = (primaryUser:User, secondaryUser:User|null) => {
    return (
      <Card className={"m-2"} title={"Result After Merge"} style={{backgroundColor:"#f5f5f5", flex:"1 1 auto", alignContent:"center"}}>
          <div className={"p-fluid p-grid"} style={{height:"20vw"}}>
            <div className={"p-col"} style={{textAlign:"center"}}>
              <Avatar image={primaryUser?.userPhotos && primaryUser?.userPhotos[0]?.imgUrl}
              imageFallback={secondaryUser?.userPhotos && secondaryUser?.userPhotos[0]?.imgUrl}
              size="xlarge"
              icon="pi pi-user"
              />
            </div>
            <ScrollPanel style={{ width: '100%', height:"20vw"}}>
              <div style={{padding:"0.5rem"}}>
                {ResultsCardColumn("DBG ID", primaryUser?.dbgId, secondaryUser?.dbgId)}
                {ResultsCardColumn("Username", primaryUser?.username, secondaryUser?.username)}
                {ResultsCardColumn("First Name", primaryUser?.firstName, secondaryUser?.firstName)}
                {ResultsCardColumn("Last Name", primaryUser?.lastName, secondaryUser?.lastName)}
                {ResultsCardColumn("Birthday", primaryUser?.userMetadata?.birthday, secondaryUser?.userMetadata?.birthday)}
                {ResultsCardColumn("Phone", primaryUser?.phone, secondaryUser?.phone)}
                {ResultsCardColumn("Email", primaryUser?.email, secondaryUser?.email)}
                {ResultsCardColumn("Is Banned?", primaryUser?.isBanned, secondaryUser?.isBanned)}
                {ResultsCardColumn("Last Logged In", primaryUser?.lastLoggedIn, secondaryUser?.lastLoggedIn)}
                {ResultsCardColumn("Country Friendly Name", primaryUser?.countryFriendlyName, secondaryUser?.countryFriendlyName)}
                {ResultsCardColumn("Country Code", primaryUser?.countryCode, secondaryUser?.countryCode)}
                {ResultsCardColumn("Approved At", primaryUser?.approvedAt, secondaryUser?.approvedAt)}
                {ResultsCardColumn("Denied At", primaryUser?.deniedAt, secondaryUser?.deniedAt)}
                {ResultsCardColumn("Occupation", primaryUser?.userMetadata?.occupationName, secondaryUser?.userMetadata?.occupationName)}
                {ResultsCardColumn("Highest Education", primaryUser?.userMetadata?.highestEducationLevel, secondaryUser?.userMetadata?.highestEducationLevel)}
                {ResultsCardColumn("Nationalities",
                  primaryUser?.userMetadata?.Nationalities?.map(x => x.EnumNationality.nationality).join(', '),
                  secondaryUser?.userMetadata?.Nationalities?.map(x => x.EnumNationality.nationality).join(', ')
                )}
                {ResultsCardColumn("Height",
                  primaryUser?.userMetadata?.heightMeasurement &&
                    formatHeightMeasurement(primaryUser?.userMetadata?.heightMeasurement).dualLocaleDisplay,
                  secondaryUser?.userMetadata?.heightMeasurement &&
                    formatHeightMeasurement(secondaryUser?.userMetadata?.heightMeasurement).dualLocaleDisplay
                )}
                {ResultsCardColumn("Preferred Sex", primaryUser?.userMetadata?.preferredSex, secondaryUser?.userMetadata?.preferredSex)}
                {ResultsCardColumn("Has/Wants Kids", primaryUser?.userMetadata?.hasOrWantsKids, secondaryUser?.userMetadata?.hasOrWantsKids)}
                {ResultsCardColumn("Smokes Tobacco", primaryUser?.userMetadata?.smokesTobacco, secondaryUser?.userMetadata?.smokesTobacco)}
                {ResultsCardColumn("Smokes Weed", primaryUser?.userMetadata?.smokesWeed, secondaryUser?.userMetadata?.smokesWeed)}
                {ResultsCardColumn("Drinks Alcohol", primaryUser?.userMetadata?.drinksAlcohol, secondaryUser?.userMetadata?.drinksAlcohol)}
                {ResultsCardColumn("Religious Belief", primaryUser?.userMetadata?.religiousBeliefName, secondaryUser?.userMetadata?.religiousBeliefName)}
                {ResultsCardColumn("Political Belief", primaryUser?.userMetadata?.politicalBeliefName, secondaryUser?.userMetadata?.politicalBeliefName)}
                {ResultsCardColumn("Body Type", primaryUser?.userMetadata?.bodyTypeName, secondaryUser?.userMetadata?.bodyTypeName)}
              </div>
            </ScrollPanel>
            <Button label="Confirm" onClick={() => selectedUser && OpenConfirmDialog()} disabled={!selectedUser}/>
          </div>
        </Card>
    )
  }

  const SelectSecondaryUserTable = (props:{data:{users:User[],total:number} | undefined}) => {
    return (
      <DataTable
        stripedRows
        lazy
        onPage={onPage}
        onFilter={onFilter}
        first={lazyState.first}
        filters={lazyState.filters}
        totalRecords={props.data ? props.data.total : 0}
        filterDisplay="menu"
        paginator
        rows={lazyState.rows}
        rowsPerPageOptions={[5, 10, 25, 50]}
        value={props.data ? props.data.users : []}
        loading={!props.data}
        tableStyle={{ minWidth: 'min-content' }}
        expandedRows={expandedRows}
        selection={selectedUser}
        selectionMode="single"
        onSelectionChange={(e) => setSelectedUser(e.value)}
        onRowToggle={(e) => {setExpandedRows(e.data)}}
        size="small"
        scrollable
        scrollHeight="20vw"
      >
        <Column selectionMode="single" headerStyle={{ width: '2rem' }}></Column>
        {columns.map((x) => <Column
          key={x.field}
          {...x}
          />
        )}
      </DataTable>
    )
  }

return (
  <>
    <Dialog header={
    <div style={{textAlign:"center"}}>
      Merge User Data into {primaryUser?.firstName} {primaryUser?.lastName}
    </div>
    }
      visible={isVisible} style={{width: "90vw"}} onHide={onHide}>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            justifyContent: "center",
            alignItems: "flex-start",
            flexWrap: "wrap"
          }}
        >
          <>{PrimaryUserCard(primaryUser)}</>
          <Card style={{width: "30vw", backgroundColor:"#f5f5f5", flex: '1 1 auto'}}>
            <div className="d-flex">
              <div className="p-2 bd-highlight">
                <h3>Secondary User Data</h3>
              </div>
              <div className="ms-auto p-2 bd-highligh">
                <IconField iconPosition="left">
                    <InputIcon className="pi pi-search" />
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Keyword Search"/>
                </IconField>
              </div>
            </div>
            <Await dependency={users}>{(data) =>
              <SelectSecondaryUserTable key="DATATABLE" data={data}/>
            }
            </Await>
          </Card>
          <>{ResultUserCard(primaryUser, selectedUser)}</>
        </div>
      </Dialog>
  </>)
}

export default MergeUsersdialog